import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getPaperTradingSession,
  getPaperTradingPositions,
  getPaperTradingTrades,
  pausePaperTradingSession,
  resumePaperTradingSession,
  stopPaperTradingSession,
} from '../api';
import Spinner from '../components/Spinner';

// BUY/SELL are the long lifecycle, SHORT/COVER are the short lifecycle.
const TRADE_ACTION_COLORS = { buy: '#2fd6c3', sell: '#f85149', short: '#d29922', cover: '#2fd6c3' };

function fmtCurrency(val, prefix = true) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  const formatted = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sign = n < 0 ? '-' : '';
  return `${sign}${prefix ? '$' : ''}${formatted}`;
}

function fmtPct(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function fmtShortDate(d) {
  if (!d) return '—';
  const dt = typeof d === 'string' ? new Date(d + 'T00:00:00') : new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function StatCard({ label, value, valueColor, subvalue }) {
  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10,
      padding: '16px 20px',
    }}>
      <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: valueColor || 'var(--text)' }}>{value}</div>
      {subvalue && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{subvalue}</div>}
    </div>
  );
}

export default function PaperTradingDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [positions, setPositions] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [showAllTrades, setShowAllTrades] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: pos }, { data: tr }] = await Promise.all([
        getPaperTradingSession(id),
        getPaperTradingPositions(id),
        getPaperTradingTrades(id),
      ]);
      setSession(s);
      setPositions(pos);
      setTrades(tr);
    } catch {
      navigate('/paper-trading');
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const handlePause = async () => {
    setActionLoading(true);
    await pausePaperTradingSession(id);
    await load();
    setActionLoading(false);
  };

  const handleResume = async () => {
    setActionLoading(true);
    await resumePaperTradingSession(id);
    await load();
    setActionLoading(false);
  };

  const handleStop = async () => {
    setActionLoading(true);
    await stopPaperTradingSession(id);
    await load();
    setActionLoading(false);
    setConfirmStop(false);
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
      <Spinner size={36} />
    </div>
  );

  if (!session) return null;

  const startCap = parseFloat(session.starting_capital);
  const curVal = parseFloat(session.current_value || session.starting_capital);
  const returnPct = ((curVal - startCap) / startCap) * 100;
  const isPositive = returnPct >= 0;

  const visibleTrades = showAllTrades ? trades : trades.slice(0, 5);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <button
            onClick={() => navigate('/paper-trading')}
            style={{
              background: 'none', border: 'none', color: 'var(--muted)', fontSize: 13,
              cursor: 'pointer', padding: 0, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            ← All strategies
          </button>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>{session.name}</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 13 }}>
            Started: {fmtDate(session.started_at)}
            {session.last_run_at && (
              <span style={{ marginLeft: 12 }}>· Last updated: {fmtDate(session.last_run_at)}</span>
            )}
          </p>
        </div>
        <StatusBadge status={session.status} />
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--border)', margin: '20px 0' }} />

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <StatCard label="Starting Capital" value={fmtCurrency(session.starting_capital)} />
        <StatCard label="Current Value" value={fmtCurrency(curVal)} />
        <StatCard
          label="Total Return"
          value={fmtPct(returnPct)}
          valueColor={isPositive ? '#2fd6c3' : '#f85149'}
        />
        <StatCard
          label="Cash Available"
          value={session.cash != null ? fmtCurrency(session.cash) : '—'}
          subvalue={`${((parseFloat(session.cash || 0) / curVal) * 100).toFixed(0)}% of portfolio`}
        />
      </div>

      {/* Open Positions */}
      <Section title="Open Positions">
        {positions.length === 0 ? (
          <p style={{ color: 'var(--muted)', margin: 0, fontSize: 13 }}>No open positions.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Symbol', 'Side', 'Shares', 'Entry Price', 'Current Price', 'Unrealized P&L', 'Return'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--muted)', fontWeight: 600, fontSize: 11 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {positions.map((pos) => {
                const qty = parseInt(pos.quantity, 10) || 0;
                const isShort = qty < 0;
                const pnl = parseFloat(pos.unrealized_pnl || 0);
                const pnlPct = parseFloat(pos.unrealized_pnl_pct || 0);
                const posColor = pnl >= 0 ? '#2fd6c3' : '#f85149';
                return (
                  <tr key={pos.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px', fontWeight: 700, color: 'var(--text)' }}>{pos.symbol}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                        background: isShort ? 'rgba(210,153,34,0.15)' : 'rgba(47,214,195,0.15)',
                        color: isShort ? '#d29922' : '#2fd6c3',
                      }}>
                        {isShort ? 'SHORT' : 'LONG'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text)' }}>{Math.abs(qty).toLocaleString()}</td>
                    <td style={{ padding: '12px', color: 'var(--text)' }}>{fmtCurrency(pos.entry_price)}</td>
                    <td style={{ padding: '12px', color: 'var(--text)' }}>
                      {pos.current_price ? fmtCurrency(pos.current_price) : '—'}
                    </td>
                    <td style={{ padding: '12px', color: posColor, fontWeight: 600 }}>
                      {pos.unrealized_pnl != null ? fmtCurrency(pnl) : '—'}
                    </td>
                    <td style={{ padding: '12px', color: posColor }}>
                      {pos.unrealized_pnl_pct != null ? fmtPct(pnlPct) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>

      {/* Recent Trades */}
      <Section title="Recent Trades">
        {trades.length === 0 ? (
          <p style={{ color: 'var(--muted)', margin: 0, fontSize: 13 }}>No trades executed yet.</p>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Date', 'Action', 'Symbol', 'Shares', 'Price', 'Commission', 'P&L'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--muted)', fontWeight: 600, fontSize: 11 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleTrades.map((trade) => {
                  const actionColor = TRADE_ACTION_COLORS[trade.event_type] || 'var(--muted)';
                  const hasPnl = trade.pnl != null;
                  const pnl = parseFloat(trade.pnl || 0);
                  return (
                    <tr key={trade.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 12px', color: 'var(--muted)' }}>{fmtShortDate(trade.executed_at)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                          background: `${actionColor}26`,
                          color: actionColor,
                        }}>
                          {trade.event_type.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text)', fontWeight: 600 }}>{trade.symbol}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text)' }}>{parseInt(trade.quantity).toLocaleString()}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text)' }}>{fmtCurrency(trade.price)}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--muted)' }}>{fmtCurrency(trade.commission)}</td>
                      <td style={{ padding: '10px 12px', color: hasPnl ? (pnl >= 0 ? '#2fd6c3' : '#f85149') : 'var(--muted)' }}>
                        {hasPnl ? fmtCurrency(pnl) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {trades.length > 5 && (
              <button
                onClick={() => setShowAllTrades(!showAllTrades)}
                style={{
                  marginTop: 12, background: 'none', border: 'none', color: 'var(--teal)',
                  fontSize: 13, cursor: 'pointer', padding: 0, fontWeight: 600,
                }}
              >
                {showAllTrades ? 'Show less ↑' : `View all ${trades.length} trades ↓`}
              </button>
            )}
          </>
        )}
      </Section>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        {session.status === 'active' && (
          <ActionBtn onClick={handlePause} disabled={actionLoading}>
            {actionLoading ? <Spinner size={14} /> : '⏸ Pause Strategy'}
          </ActionBtn>
        )}
        {session.status === 'paused' && (
          <ActionBtn onClick={handleResume} disabled={actionLoading} style={{ borderColor: 'var(--teal)', color: 'var(--teal)' }}>
            {actionLoading ? <Spinner size={14} /> : '▶ Resume Strategy'}
          </ActionBtn>
        )}
        {session.status !== 'stopped' && (
          confirmStop ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>Are you sure?</span>
              <ActionBtn onClick={handleStop} disabled={actionLoading} style={{ borderColor: '#f85149', color: '#f85149' }}>
                Confirm Stop
              </ActionBtn>
              <ActionBtn onClick={() => setConfirmStop(false)}>Cancel</ActionBtn>
            </div>
          ) : (
            <ActionBtn onClick={() => setConfirmStop(true)} style={{ borderColor: '#f85149', color: '#f85149' }}>
              ✕ Stop Strategy
            </ActionBtn>
          )
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
      padding: 24, marginBottom: 16,
    }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    active: { bg: 'rgba(47,214,195,0.15)', color: '#2fd6c3', border: 'rgba(47,214,195,0.4)' },
    paused: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: 'rgba(245,158,11,0.4)' },
    stopped: { bg: 'rgba(248,81,73,0.15)', color: '#f85149', border: 'rgba(248,81,73,0.4)' },
  };
  const c = colors[status] || colors.stopped;
  return (
    <span style={{
      padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
    }}>{status}</span>
  );
}

function ActionBtn({ children, onClick, disabled, style }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)',
      padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
      opacity: disabled ? 0.6 : 1, ...style,
    }}>
      {children}
    </button>
  );
}
